import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components';
import Pagination from 'shared/data-display/Pagination';
import { usePagination } from 'app/utils/hooks/usePagination';
import { useEffect, useMemo, useState } from 'react';
import { TagAction, TagItemShortcuts } from '../TagItemShortcuts/TagItemShortcuts';
import { TagTable } from '../TagTable/TagTable';
import { DismissModal } from '../DismissModal/DismissModal';
import {
  TagWithMetadata,
  UUID,
  useDocumentTaxonomyConfig,
  useMandatoryStatus,
  useMandatoryProgress,
  useDismissMandatoryConcept,
  useUndismissMandatoryConcept,
  DocumentTaxonomyConfigTaxonomy,
  MandatoryStatusEntry,
} from 'entities/tag';
import { formatTaxonomyName, resolveConceptLabel } from 'app/utils/tag-utils';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { TagLanguageDropdown } from 'app/components/editor/sidebar/tags/TagLanguageDropdown';

import './Mandatory.scss';

interface MandatoryProps {
  documentId: string;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
}

interface MandatoryRow {
  conceptId: UUID;
  label: string;
  conceptName: string;
  taxonomyId: UUID;
  taxonomyName: string;
  tagged: boolean;
  dismissed: boolean;
}

interface TaxonomyStatusProps {
  documentSuperId: number;
  selection: DocumentTaxonomyConfigTaxonomy;
  onRows: (taxonomyId: UUID, rows: MandatoryRow[]) => void;
}

const TaxonomyStatusLoader = ({ documentSuperId, selection, onRows }: TaxonomyStatusProps) => {
  const taxonomyId = selection.taxonomyId;
  const taxonomyName = selection.taxonomy?.name ? formatTaxonomyName(selection.taxonomy.name) : taxonomyId;
  const { data } = useMandatoryStatus(documentSuperId, taxonomyId);
  const { tagLanguage } = useTagDisplay();

  useEffect(() => {
    if (!data) return;
    const rows: MandatoryRow[] = data.map((entry: MandatoryStatusEntry) => ({
      conceptId: entry.conceptId,
      conceptName: entry.name,
      label: entry.labels ? resolveConceptLabel({ languages: entry.labels, currentLang: tagLanguage }) || entry.name : entry.name,
      taxonomyId,
      taxonomyName,
      tagged: entry.tagged,
      dismissed: entry.dismissal !== null,
    }));
    onRows(taxonomyId, rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tagLanguage]);

  return null;
};

export const Mandatory = ({ documentId }: MandatoryProps) => {
  const { t } = useTranslation(['all', 'tag', 'taxonomy']);
  const { tagLanguage } = useTagDisplay();

  const documentSuperId = parseInt(documentId, 10);
  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('asc');
  const [rowsByTaxonomy, setRowsByTaxonomy] = useState<Record<UUID, MandatoryRow[]>>({});
  const [activeTaxonomyIds, setActiveTaxonomyIds] = useState<UUID[]>([]);
  const [dismissModal, setDismissModal] = useState<{ show: boolean; row: MandatoryRow | null }>({ show: false, row: null });

  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(Number.isFinite(documentSuperId) ? documentSuperId : undefined);
  const selections = taxonomyConfig?.taxonomySelections ?? [];

  const dismiss = useDismissMandatoryConcept();
  const undismiss = useUndismissMandatoryConcept();

  const initialActiveTaxonomyKey = selections.map((s) => s.taxonomyId).join(',');
  useEffect(() => {
    if (selections.length > 0 && activeTaxonomyIds.length === 0) {
      setActiveTaxonomyIds(selections.map((s) => s.taxonomyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialActiveTaxonomyKey]);

  const handleRows = (taxonomyId: UUID, rows: MandatoryRow[]) => {
    setRowsByTaxonomy((prev) => (prev[taxonomyId] === rows ? prev : { ...prev, [taxonomyId]: rows }));
  };

  const onTaxonomyChange = (taxonomyId: UUID) => {
    setActiveTaxonomyIds((prev) => (prev.includes(taxonomyId) ? prev.filter((t) => t !== taxonomyId) : [...prev, taxonomyId]));
  };

  const singleActiveTaxonomyId = activeTaxonomyIds.length === 1 ? activeTaxonomyIds[0] : undefined;
  const { data: progress } = useMandatoryProgress(Number.isFinite(documentSuperId) ? documentSuperId : undefined, singleActiveTaxonomyId);

  const combinedProgress = useMemo(() => {
    if (progress && singleActiveTaxonomyId) return progress;
    let total = 0;
    let addressed = 0;
    let tagged = 0;
    let dismissed = 0;
    for (const taxonomyId of activeTaxonomyIds) {
      const rows = rowsByTaxonomy[taxonomyId] ?? [];
      total += rows.length;
      for (const r of rows) {
        if (r.tagged) tagged++;
        if (r.dismissed) dismissed++;
        if (r.tagged || r.dismissed) addressed++;
      }
    }
    return {
      total,
      tagged,
      dismissed,
      addressed,
      percentage: total === 0 ? 0 : Math.round((addressed / total) * 100),
    };
  }, [progress, singleActiveTaxonomyId, activeTaxonomyIds, rowsByTaxonomy]);

  const sortedRows = useMemo(() => {
    const rows: MandatoryRow[] = [];
    for (const taxonomyId of activeTaxonomyIds) {
      rows.push(...(rowsByTaxonomy[taxonomyId] ?? []));
    }
    return rows.sort((a, b) => (sortBy === 'asc' ? a.label.localeCompare(b.label, tagLanguage) : b.label.localeCompare(a.label, tagLanguage)));
  }, [activeTaxonomyIds, rowsByTaxonomy, sortBy, tagLanguage]);

  const { paginatedItems, page, setCurrentPage, pageSize } = usePagination(sortedRows);

  const tagCategories = [t('tag:name'), t('taxonomy:title'), t('tag:is.used')];

  const onDismissSubmit = (reason: string) => {
    const row = dismissModal.row;
    if (!row || !Number.isFinite(documentSuperId)) return;
    dismiss.mutate({ documentSuperId, conceptId: row.conceptId, message: reason });
    setDismissModal({ show: false, row: null });
  };

  const onUndismiss = (row: MandatoryRow) => {
    if (!Number.isFinite(documentSuperId)) return;
    undismiss.mutate({ documentSuperId, conceptId: row.conceptId });
  };

  return (
    <div className="tagging-mandatory">
      {selections.map((s) => (
        <TaxonomyStatusLoader key={s.taxonomyId} documentSuperId={documentSuperId} selection={s} onRows={handleRows} />
      ))}

      <div className="tagging-mandatory__toolbar">
        <section className="tagging-mandatory__taxonomy-options">
          {selections.map((selection) => {
            const name = selection.taxonomy?.name ? formatTaxonomyName(selection.taxonomy.name) : selection.taxonomyId;
            return (
              <div key={selection.taxonomyId} className="tagging-mandatory__taxonomy-option">
                <input
                  id={selection.taxonomyId}
                  type="checkbox"
                  checked={activeTaxonomyIds.includes(selection.taxonomyId)}
                  onChange={() => onTaxonomyChange(selection.taxonomyId)}
                />
                <Typography variant="body-7" fontWeight="semibold">
                  {name}
                </Typography>
              </div>
            );
          })}
        </section>
        <TagLanguageDropdown documentSuperId={Number.isFinite(documentSuperId) ? documentSuperId : undefined} />
      </div>

      <div className="tagging-mandatory__progress">
        <Typography variant="body-7" fontWeight="semibold">
          {t('tag:mandatory.tags')}
        </Typography>
        <Typography variant="body-7" fontWeight="light">
          {`${combinedProgress.addressed} / ${combinedProgress.total} (${combinedProgress.percentage}%)`}
        </Typography>
      </div>

      <TagTable categories={tagCategories} align="center" setSortBy={() => setSortBy((p) => (p === 'asc' ? 'desc' : 'asc'))} sortBy={sortBy}>
        {paginatedItems.length > 0 ? (
          paginatedItems.map((row) => {
            const tagActions: TagAction[] = [];
            const addressed = row.tagged || row.dismissed;

            if (row.dismissed) {
              tagActions.push({ type: 'dismiss', onClick: () => onUndismiss(row), label: t('tag:mandatory.undismiss') });
            } else if (!row.tagged) {
              tagActions.push({ type: 'dismiss', onClick: () => setDismissModal({ show: true, row }), label: t('tag:mandatory.dismiss') });
            }

            return (
              <tr key={`${row.taxonomyId}:${row.conceptId}`}>
                <td>
                  <Typography variant="body-7" fontWeight="semibold" color="#37353B">
                    {row.label}
                  </Typography>
                </td>
                <td className="align-center">
                  <Typography variant="body-7" fontWeight="semibold" color="#6B7076">
                    {row.taxonomyName}
                  </Typography>
                </td>
                <td className="align-center">
                  <span className={addressed ? 'used' : 'not-used'}>
                    {addressed ? <i className="fa-solid fa-check" /> : <i className="fa-regular fa-ban" />}
                  </span>
                </td>
                <td>
                  <TagItemShortcuts actions={tagActions} />
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={3} className="align-center">
              <Typography variant="body-7">{t('all:tags.empty.list')}</Typography>
            </td>
          </tr>
        )}
      </TagTable>

      <div className="d-flex justify-content-center mt-3">
        <Pagination onPaginate={(p: number) => setCurrentPage(p)} page={page} pageSize={pageSize} />
      </div>

      <DismissModal show={dismissModal.show} onClose={() => setDismissModal({ show: false, row: null })} onConfirm={onDismissSubmit} type="mandatory" />
    </div>
  );
};
